import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import ImgWithTitle from "../../component/workspaceContainer/ImgWithTitle";
import TextInput from "../../component/textInput/TextInput";
import { PrimaryButton } from "../../component/buttonComponent/ButtonComponent";
import "./ForgotPassword.css";
import PasswordStrengthBar from "../../component/passwordStrengthBar/PasswordStrengthBar";
import FooterImage from "../../image/header.png";
import CryptoJS from "crypto-js";
import SnackBar from "../../component/Snackbar/Snackbar";
import Cookies from "js-cookie";
import {
  APIResetPassword,
  APIResetParentPassword,
} from "../../config/utilities/APIs";

const emailKey = process.env.REACT_APP_EMAIL_SECRET_KEY;

const SetNewPassword = () => {
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorVerify, setPasswordErrorVerify] = useState(false);
  const [passwordErrorMessageVerify, setPasswordErrorMessageVerify] =
    useState("");
  const [keyUp, setKeyUp] = useState("", false);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState({
    password: "",
    passwordVerify: "",
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [email, setEmail] = useState("");
  const [userData, setUserData] = useState(null);

  const navigate = useNavigate();

  const params = useParams();
  const token = params["*"];

  const decryptToken = (encryptedToken) => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, emailKey);
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

      return decryptedData;
    } catch (error) {
      console.error("Decryption failed: ", error.message);
      return null;
    }
  };

  useEffect(() => {
    const userData = token ? decryptToken(token) : null;
    setUserData(userData);

    if (!userData || new Date(userData.linkExpiry) < new Date()) {
      navigate("/invalidresetlink", { replace: true });
    } else {
      setEmail(userData.email);
    }
  }, [token, navigate]);

  const resetPassword = async () => {
    setLoading(true);

    const isParent = userData?.parentId !== undefined;

    const adminData = {
      email,
      password: input.passwordVerify,
    };

    try {
      const response = isParent
        ? await APIResetParentPassword(adminData, setLoading) // Parent reset API
        : await APIResetPassword(adminData, setLoading); // Admin reset API

      if (response.status === 200) {
        Cookies.remove("resetEmail");
        if (isParent) {
          sessionStorage.setItem("resetUserType", "parent");
        } else {
          sessionStorage.setItem("resetUserType", "admin");
        }

        navigate("/confirmreset", { replace: true });
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to reset password"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Reset Password Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      setLoading(false);
    }
  };

  const passwordOnChangeFunction = (e) => {
    setInput({ ...input, password: e.target.value });
  };

  const verifyPasswordOnChangeFunction = (e) => {
    const newPasswordVerify = e.target.value;
    setInput({ ...input, passwordVerify: newPasswordVerify });
  };

  useEffect(() => {
    if (input.password && input.passwordVerify) {
      if (input.password !== input.passwordVerify) {
        setPasswordErrorVerify(true);
        setPasswordErrorMessageVerify("Passwords do not match");
      } else {
        setPasswordErrorVerify(false);
        setPasswordErrorMessageVerify("");
      }
    }
  }, [input.password, input.passwordVerify]);

  const handleStrengthChange = (strength) => {
    setPasswordStrength(strength);
  };

  const isResetDisabled =
    !input.password ||
    !input.passwordVerify ||
    passwordStrength < 100 ||
    input.password != input.passwordVerify;

  return (
    <>
      <div className="forgot-password-container-main">
        <div className="forgot-password-container">
          <div className="img-with-title-container">
            <ImgWithTitle
              titleText="Set new password"
              subText=""
              icon="Users"
            />
          </div>
          <div className="reset-new-password-column">
            <div className="reset-password-input-container">
              <TextInput
                label="Password"
                placeholder="Enter new password"
                setInput={setInput}
                input={input}
                name="password"
                type="password"
                error={passwordError}
                setError={setPasswordError}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                onChangeFunction={passwordOnChangeFunction}
              />
            </div>
            <div className="reset-password-input-container">
              <TextInput
                label="Verify Password"
                placeholder="Verify new password"
                setInput={setInput}
                input={input}
                name="passwordVerify"
                type="password"
                error={passwordErrorVerify}
                setError={setPasswordErrorVerify}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                onChangeFunction={verifyPasswordOnChangeFunction}
                errorMessage={passwordErrorMessageVerify}
              />
              <div className="password-strength-bar">
                {input.password && (
                  <PasswordStrengthBar
                    password={input.password}
                    onStrengthChange={handleStrengthChange}
                  />
                )}
              </div>
            </div>
          </div>
          <div
            className="forgot-password-reset-button"
            style={{ paddingTop: "1rem" }}
          >
            <PrimaryButton
              label="Reset password"
              disable={isResetDisabled}
              onClickFunction={resetPassword}
              loading={loading}
            />
          </div>
        </div>
        <div className="login-logo-footer">
          <div className="login-logo">
            <img
              loading="lazy"
              src={FooterImage}
              className="logo"
              alt="header-logo"
            />
          </div>
        </div>
      </div>
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </>
  );
};

export default SetNewPassword;
